import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Markdown from "@/components/Markdown";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Search, Plus, GraduationCap, Trash2, NotebookPen, ArrowUpRight, Archive, Sparkles, ThumbsUp, Wrench } from "lucide-react";

const KA = ["Scope", "Schedule", "Cost", "Quality", "Resources", "Communications", "Risk", "Procurement", "Stakeholders", "Technical", "Other"];
const SEV = { low: "bg-slate-200 text-slate-600", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-600" };
const blankForm = { project_id: "", title: "", lesson_type: "went_well", category: "Other", situation: "", impact: "", severity: "medium", recommendation: "" };

export default function LessonsPage() {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [assessPid, setAssessPid] = useState("");
  const [assessBusy, setAssessBusy] = useState(false);
  const [viewAssess, setViewAssess] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/lessons").then(({ data }) => setData(data)).catch(() => setData({ lessons: [], minutes: [], assessments: [] }));
  useEffect(() => {
    load();
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.lessons;
    return data.lessons.filter((l) => `${l.title} ${l.situation} ${l.recommendation} ${l.category} ${l.project_name}`.toLowerCase().includes(needle));
  }, [data, q]);

  const save = async () => {
    if (!form.project_id || !form.title.trim()) { toast.error("Pick a project and give the lesson a title"); return; }
    setSaving(true);
    try {
      await api.post("/lessons", form);
      toast.success("Lesson captured");
      setOpen(false);
      setForm(blankForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the lesson"); }
    finally { setSaving(false); }
  };

  const remove = async (l) => {
    try {
      await api.delete(`/lessons/${l.id}`);
      toast.success("Lesson removed");
      load();
    } catch { toast.error("Could not remove the lesson"); }
  };

  const runAssessment = async () => {
    if (!assessPid) { toast.error("Pick a project to assess"); return; }
    setAssessBusy(true);
    try {
      const { data: res } = await api.post("/lessons/assessment", { project_id: assessPid });
      setViewAssess({ ...res, project_name: projects.find((p) => p.id === assessPid)?.name || "" });
      toast.success("Assessment generated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate the assessment"); }
    finally { setAssessBusy(false); }
  };

  return (
    <Layout>
      <div className="space-y-10" data-testid="lessons-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight">Lessons Learned</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">An industry-standard lessons learned register — capture what each project teaches you, by knowledge area, and feed the PMO knowledge base.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the register…" className="pl-9" data-testid="lessons-search" />
            </div>
            <Button className="gap-1.5 shrink-0" onClick={() => setOpen(true)} data-testid="lessons-add-button">
              <Plus className="h-4 w-4" /> Add lesson
            </Button>
          </div>
        </div>

        <section className="rounded-2xl border border-violet-200 bg-violet-50/50 p-5" data-testid="lessons-assessment">
          <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> AI Assessment (industry template)</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Generates a full industry-standard lessons-learned assessment from real project data — performance, what went well, improvement areas, lessons by knowledge area and recommendations.</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Select value={assessPid} onValueChange={setAssessPid}>
              <SelectTrigger className="w-64 bg-white" data-testid="assessment-project-select"><SelectValue placeholder="Pick a project to assess…" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={runAssessment} disabled={assessBusy || !assessPid} className="gap-1.5 bg-violet-600 hover:bg-violet-700" data-testid="assessment-generate">
              {assessBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Assessing…</> : <><Sparkles className="h-4 w-4" /> Generate assessment</>}
            </Button>
          </div>
          {data?.assessments?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {data.assessments.map((a) => (
                <button key={a.project_id} onClick={() => setViewAssess(a)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-violet-300 bg-white text-violet-800 px-3 py-1 hover:bg-violet-100 transition-colors"
                  data-testid={`assessment-chip-${a.project_id}`}>
                  <GraduationCap className="h-3 w-3" /> {a.project_name} · {new Date(a.generated_at).toLocaleDateString()}
                </button>
              ))}
            </div>
          )}
        </section>

        {data === null && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {data !== null && (
          <>
            {data.lessons.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="lessons-empty">
                <GraduationCap className="h-8 w-8 text-primary mx-auto mb-3" />
                <p className="text-muted-foreground">The register is empty. Capture your first lesson — what went well, what you'd do differently, and the recommendation for next time.</p>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-white overflow-x-auto" data-testid="lessons-register">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2.5 font-bold">Date</th>
                      <th className="px-3 py-2.5 font-bold">Category</th>
                      <th className="px-3 py-2.5 font-bold">Type</th>
                      <th className="px-3 py-2.5 font-bold">Lesson / Situation</th>
                      <th className="px-3 py-2.5 font-bold">Impact</th>
                      <th className="px-3 py-2.5 font-bold">Recommendation</th>
                      <th className="px-3 py-2.5 font-bold">Project</th>
                      <th className="px-2 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No lessons match "{q}".</td></tr>
                    )}
                    {shown.map((l) => (
                      <tr key={l.id} className="border-b border-border/60 last:border-0 align-top group" data-testid={`lesson-${l.id}`}>
                        <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-3 py-2.5"><span className="text-xs font-semibold bg-secondary rounded-full px-2 py-0.5 whitespace-nowrap">{l.category}</span></td>
                        <td className="px-3 py-2.5">
                          {l.lesson_type === "went_well"
                            ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 whitespace-nowrap"><ThumbsUp className="h-3 w-3" /> Went well</span>
                            : <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 whitespace-nowrap"><Wrench className="h-3 w-3" /> Improve</span>}
                        </td>
                        <td className="px-3 py-2.5 max-w-[260px]">
                          <p className="font-semibold">{l.title}</p>
                          {l.situation && <p className="text-xs text-muted-foreground mt-0.5">{l.situation}</p>}
                        </td>
                        <td className="px-3 py-2.5 max-w-[180px]">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV[l.severity] || SEV.medium}`}>{l.severity}</span>
                          {l.impact && <p className="text-xs text-muted-foreground mt-1">{l.impact}</p>}
                        </td>
                        <td className="px-3 py-2.5 max-w-[220px] text-xs text-muted-foreground">{l.recommendation || "—"}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">{l.project_archived && <Archive className="h-3 w-3" />} {l.project_name}</span>
                        </td>
                        <td className="px-2 py-2.5">
                          <button onClick={() => remove(l)} className="text-muted-foreground/40 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" title="Delete lesson" data-testid={`lesson-delete-${l.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.minutes.length > 0 && (
              <section data-testid="lessons-minutes">
                <h2 className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2 mb-1">
                  <NotebookPen className="h-4 w-4 text-primary" /> From meeting minutes
                </h2>
                <p className="text-sm text-muted-foreground mb-4">Recent minutes often hide lessons worth capturing — click through to review.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.minutes.slice(0, 8).map((m) => (
                    <button key={m.id} onClick={() => nav(`/projects/${m.project_id}?tab=notebook`)}
                      className="text-left rounded-lg border border-border bg-white p-3.5 hover:border-primary/40 transition-colors group"
                      data-testid={`lessons-minutes-${m.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm truncate">{m.title}</p>
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.snippet}</p>
                      <p className="text-[11px] text-muted-foreground mt-1.5">{m.project_name}{m.meeting_date ? ` · ${m.meeting_date}` : ""}</p>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="lesson-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><GraduationCap className="h-5 w-5 text-primary" /> Capture a lesson</DialogTitle>
              <DialogDescription>Lessons-learned register entry — searchable in the PMO knowledge layer.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="lesson-project-select"><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.lesson_type} onValueChange={(v) => setForm((f) => ({ ...f, lesson_type: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="lesson-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="went_well">What went well</SelectItem>
                      <SelectItem value="needs_improvement">Needs improvement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Knowledge area</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="lesson-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {KA.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Impact severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="lesson-severity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="lesson-title">Lesson title</Label>
                <Input id="lesson-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Vendor onboarding needs 3 extra weeks of lead time" className="mt-1.5" data-testid="lesson-title-input" />
              </div>
              <div>
                <Label htmlFor="lesson-situation">What happened (situation)</Label>
                <Textarea id="lesson-situation" rows={2} value={form.situation} onChange={(e) => setForm((f) => ({ ...f, situation: e.target.value }))} placeholder="Describe the situation and root cause…" className="mt-1.5" data-testid="lesson-situation-input" />
              </div>
              <div>
                <Label htmlFor="lesson-impact">Impact</Label>
                <Input id="lesson-impact" value={form.impact} onChange={(e) => setForm((f) => ({ ...f, impact: e.target.value }))} placeholder="e.g. 2-week schedule slip on integration phase" className="mt-1.5" data-testid="lesson-impact-input" />
              </div>
              <div>
                <Label htmlFor="lesson-rec">Recommendation</Label>
                <Textarea id="lesson-rec" rows={2} value={form.recommendation} onChange={(e) => setForm((f) => ({ ...f, recommendation: e.target.value }))} placeholder="What should future projects do differently?" className="mt-1.5" data-testid="lesson-recommendation-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} className="gap-2" data-testid="lesson-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save lesson
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewAssess} onOpenChange={(v) => !v && setViewAssess(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="assessment-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> Lessons Learned Assessment</DialogTitle>
              <DialogDescription>
                {viewAssess?.project_name}{viewAssess?.generated_at ? ` · generated ${new Date(viewAssess.generated_at).toLocaleString()}` : ""}
              </DialogDescription>
            </DialogHeader>
            <Markdown text={viewAssess?.summary} />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
